import { VdTracking } from '@vend/vend.js'
import angular, { IScope } from 'angular'
import includes from 'lodash/includes'
import pull from 'lodash/pull'
import { ApiButtonLayout } from '@vend/types'

import template from '../../../templates/settings/wr-qk-layouts.html'
import { STATES } from '../../../../lib/routes'
import { $q, $state } from '../../ng-compat/imports'
import { QuickKeysManager } from '../../managers/quick-keys-manager'
import { RegisterManager } from '../../managers/register-manager'
import { registersStore } from '../../factories/database/registers-store'
import { quickKeyLayoutsStore } from '../../factories/database/quick-key-layouts-store'

/**
 * Directive for showing all available Quick Key layouts for the current register.
 *
 * @module webregister.directives.wrQkLayouts
 */
angular.module('webregister.directives').directive('wrQkLayouts', function () {
  return {
    restrict: 'E',
    template,
    bindToController: true,
    controllerAs: 'qkLayoutsCtrl',
    /**
     * =======================================
     * QUICK KEY LAYOUTS CONTROLLER
     * =======================================
     */
    controller(
      this: $TSFixMe,
      $scope: IScope,
      quickKeysManager: QuickKeysManager,
      registerManager: RegisterManager,
      vdTracking: VdTracking,
      vdToastNotificationService,
      Confirm
    ) {
      const SET_CURRENT_ERROR_MSG = 'setting this layout as the current layout'
      const DELETE_ERROR_MSG = 'deleting this layout'
      const DUPLICATE_ERROR_MSG = 'duplicating this layout'
      const DUPLICATE_QUEUED_MSG =
        'Layout queued for duplication and will appear shortly'

      // Used to identify in the UI which layout is currently being actioned (deleted, duplicated etc).
      const layoutsBeingProcessed: $TSFixMe[] = []
      /**
       * Tracks a quick key layout action
       *
       * @private
       * @method trackQuickKeyLayoutActions
       *
       * @param  eventType {String}
       *         The event that has occured
       */
      function trackQuickKeyLayoutActions(eventType: string) {
        vdTracking.trackUserEvent({
          eventName: 'Quick Key Actions',
          metadata: {
            Event: eventType,
          },
        })
      }

      /**
       * Set the selected quick key button layout as the current, active layout.
       *
       * @method setCurrentLayout
       *
       * @param  quickKeyLayout {QuickKeyLayout}
       *         The quick key button layout which is to be set as the current layout.
       *
       * @return {Promise} A promise resolved once the button layout has been set as the current layout.
       */
      this.setCurrentLayout = function (quickKeyLayout: ApiButtonLayout) {
        trackQuickKeyLayoutActions('Set As Current Layout')
        layoutsBeingProcessed.push(quickKeyLayout.id)
        return registerManager
          .setCurrentQuickKeyLayout(quickKeyLayout)
          .then(() => this._removeLayoutFromInProcessList(quickKeyLayout))
          .catch(() =>
            this._notifyAndResetLayoutProcessed({
              message: SET_CURRENT_ERROR_MSG,
              quickKeyLayout,
            })
          )
      }

      /**
       * Deletes the selected quick key button layout, only if the selected button layout is not being used by other
       * registers.
       *
       * @method deleteLayout
       *
       * @param  quickKeyLayout {QuickKeyLayout}
       *         The quick key button layout to delete
       *
       * @return {Promise} A promise resolved once the button layout has been deleted.
       */
      this.deleteLayout = function (quickKeyLayout: ApiButtonLayout) {
        trackQuickKeyLayoutActions('Delete Layout')
        if (this.quickKeyLayoutsInUse[quickKeyLayout.id]) {
          const registerNames = this.quickKeyLayoutsInUse[
            quickKeyLayout.id
          ].map((register: $TSFixMe) => register.name)
          vdToastNotificationService.negative({
            message: `Unable to delete this layout as it's currently being used by ${registerNames.join(
              ', '
            )}.`,
          })

          return $q.reject()
        }

        return Confirm({
          header: 'You are about to delete this Quick Key Layout',
          content:
            'Other registers and outlets will no longer be able to use this layout in the future.',
          cancel: 'Don’t Delete',
          confirm: {
            label: 'Delete',
            category: 'no',
          },
        }).then(() => {
          layoutsBeingProcessed.push(quickKeyLayout.id)
          return quickKeysManager
            .deleteLayout(quickKeyLayout)
            .then(() => this._removeLayoutFromInProcessList(quickKeyLayout))
            .catch(() =>
              this._notifyAndResetLayoutProcessed({
                message: DELETE_ERROR_MSG,
                quickKeyLayout,
              })
            )
        })
      }

      /**
       * Duplicate the selected quick key button layout.
       *
       * @method duplicateLayout
       *
       * @param  quickKeyLayout {QuickKeyLayout}
       *         The quick key button layout to duplicate
       *
       * @return {Promise} A promise resolved once the button layout has been duplicated.
       */
      this.duplicateLayout = function (quickKeyLayout: ApiButtonLayout) {
        trackQuickKeyLayoutActions('Duplicate Layout')
        layoutsBeingProcessed.push(quickKeyLayout.id)

        return quickKeysManager
          .duplicateLayout(quickKeyLayout)
          .then(() => {
            vdToastNotificationService.success({
              message: DUPLICATE_QUEUED_MSG,
            })
            this._removeLayoutFromInProcessList(quickKeyLayout)
          })
          .catch(() =>
            this._notifyAndResetLayoutProcessed({
              message: DUPLICATE_ERROR_MSG,
              quickKeyLayout,
            })
          )
      }

      /**
       * Navigate to the 'edit' quick key button layout state.
       *
       * @method editLayout
       *
       * @param  quickKeyLayout {QuickKeyLayout}
       *         The quick key button layout to edit
       */
      this.editLayout = function (quickKeyLayout: ApiButtonLayout) {
        trackQuickKeyLayoutActions('Edit Layout')

        $state.go(STATES.SETTINGS_QUICK_KEYS_EDIT, {
          layoutId: quickKeyLayout.id,
        })
      }

      /**
       * Navigate to Add Quick Key layout state.
       *
       * @method addNewLayout
       */
      this.addNewLayout = function () {
        trackQuickKeyLayoutActions('Add New Layout')
        this.awaitingServerResponse = true

        quickKeysManager
          .createLayout(quickKeysManager.defaultNewLayoutName)
          .then(
            (quickKeyLayout: $TSFixMe) =>
              quickKeysManager.navigateToEditState(quickKeyLayout, true),
            () => {
              this.awaitingServerResponse = false
            }
          )
      }

      /**
       * Whether the provided quick key layout is the current, active layout for the register.
       *
       * @method isCurrentLayout
       *
       * @param  quickKeyLayout {QuickKeyLayout}
       *         The quickKeyLayout to evaluate
       *
       * @return {Boolean} True if the provided quick layout is the current active layout
       */
      this.isCurrentLayout = function (quickKeyLayout: ApiButtonLayout) {
        return quickKeysManager.isCurrentLayout(quickKeyLayout)
      }

      /**
       * Helper method to identify if the provided quick key button layout is currently being actioned / processed.
       *
       * @method isLayoutBeingProcessed
       *
       * @param  quickKeyLayout {QuickKeyLayout}
       *         The quickKeyLayout to evaluate
       *
       * @return {Boolean} True if the layout is being processed, false otherwise.
       */
      this.isLayoutBeingProcessed = function (quickKeyLayout: ApiButtonLayout) {
        return includes(layoutsBeingProcessed, quickKeyLayout.id)
      }

      /**
       * Order the retrieved quick key layouts to ensure current layout is always at the top of the list, with the
       * remaining layouts sorted alphabetically by their name.
       *
       * @method orderQuickKeyLayout
       *
       * @param  quickKeyLayout {QuickKeyLayout}
       *         The quickKeyLayout to evaluate
       *
       * @return {String|Number} Either the layout name, or 0 if the quick key layout is the current layout.
       */
      this.orderQuickKeyLayout = (quickKeyLayout: ApiButtonLayout) => {
        if (quickKeysManager.isCurrentLayout(quickKeyLayout)) {
          return 0
        } else {
          return quickKeyLayout.name
        }
      }

      // ============================== Private API ======================== //

      /**
       * Remove the provided layout from the array of layouts which are currently being actioned / processed.
       *
       * @private
       * @method _removeLayoutFromInProcessList
       *
       * @param  quickKeyLayout {QuickKeyLayout}
       *         The quickKeyLayout to remove from the layouts being processed
       *
       * @return {Array} layoutsBeingProcessed without the provided layout
       */
      this._removeLayoutFromInProcessList = function (
        quickKeyLayout: ApiButtonLayout
      ) {
        return pull(layoutsBeingProcessed, quickKeyLayout.id)
      }

      /**
       * Responsible for handling the error messages when performing the various actions on each layout.
       *
       * @private
       * @method _notifyAndResetLayoutProcessed
       *
       * @param options {Object}
       *
       *        @param message {String}
       *               The message to be interpolated
       *
       *        @param quickKeyLayout {QuickKeyLayout}
       *               The quick key layout affected
       */
      this._notifyAndResetLayoutProcessed = function ({
        message,
        quickKeyLayout,
      }: any) {
        this._removeLayoutFromInProcessList(quickKeyLayout)

        vdToastNotificationService.negative({
          message: `Sorry there was a problem ${message}, please try again.`,
        })
      }

      /**
       * Returns all quick key button layouts currently set as the active layout for other registers. Represented by
       * the button layout id as key, with an array of registers as value.
       *
       * @private
       * @method _retrieveLayoutsInUseByRegisters
       *
       * @return {Promise<Object>} Resolved once the layouts that are in use by other registers have been retrieved.
       */
      this._retrieveLayoutsInUseByRegisters = function () {
        const activeRegister = registerManager.getActiveRegister()
        return registersStore.all().then(registers => {
          this.quickKeyLayoutsInUse = {}
          registers.forEach(register => {
            const quickKeyLayoutId = register.button_layout_id

            if (
              (activeRegister && activeRegister.id === register.id) ||
              !quickKeyLayoutId
            ) {
              return
            }
            if (!this.quickKeyLayoutsInUse[quickKeyLayoutId]) {
              this.quickKeyLayoutsInUse[quickKeyLayoutId] = [register]
            } else {
              this.quickKeyLayoutsInUse[quickKeyLayoutId].push(register)
            }
          })
          return this.quickKeyLayoutsInUse
        })
      }

      /**
       * Responsible for retrieving the list of quick key layouts from the store.
       *
       * @private
       * @method _retrieveQuickKeyLayouts
       *
       * @return {Promise<Array>} Resolved once the latest quick key layouts have been retrieved from the store.
       */
      this._retrieveQuickKeyLayouts = function () {
        return quickKeysManager
          .getAllQuickKeyLayouts()
          .then((quickKeyLayouts: $TSFixMe) => {
            this.activeQuickKeyLayout = quickKeysManager.quickKeyLayout
            this.quickKeyLayouts = quickKeyLayouts
            return this.quickKeyLayouts
          })
      }

      /**
       * Construct the required data for the list of quick key layouts.
       *
       * @private
       * @method _buildQuickKeyLayoutData
       *
       * @return {Promise} Once _updateLayoutsInUseByRegisters and _retrieveQuickKeyLayouts have been resolved.
       */
      this._buildQuickKeyLayoutData = function () {
        return $q.all([
          this._retrieveLayoutsInUseByRegisters(),
          this._retrieveQuickKeyLayouts(),
        ])
      }

      quickKeysManager.init().then(() => {
        this._buildQuickKeyLayoutData()
        registersStore.on('sync', this._buildQuickKeyLayoutData, this)
        quickKeyLayoutsStore.on('sync', this._buildQuickKeyLayoutData, this)

        $scope.$on('$destroy', () => {
          registersStore.off('sync', this._buildQuickKeyLayoutData)
          quickKeyLayoutsStore.off('sync', this._buildQuickKeyLayoutData)
        })
      })
    },
  }
})
